import React from 'react';
import EmploymentAttestationSummary from '@/components/hiring/EmploymentAttestationSummary';
import RequirementAnswersSummary from '@/components/hiring/RequirementAnswersSummary';
import SharedAnswersSummary from '@/features/applications/student/blocks/SharedAnswersSummary';
import { getProgramDefinition } from '@/features/applications/student/programs';
import ResumeAnswer from '@/features/applications/student/programs/ResumeAnswer';
import { candidacyService } from '@/shared/services/candidacyService';

// A stored answer keeps its line breaks, one paragraph per line.
export function Paragraphs({ value }) {
    return String(value)
        .split('\n')
        .filter((line) => line.trim() !== '')
        .map((line, index, lines) => (
            <p
                className={index === lines.length - 1 ? 'mb-0' : 'mb-2'}
                key={index}
            >
                {line}
            </p>
        ));
}

function Field({ label, children }) {
    return (
        <div className="row mb-2">
            <dt className="col-sm-4 fw-normal text-body-secondary">{label}</dt>
            <dd className="col-sm-8 mb-0">{children}</dd>
        </div>
    );
}

const timeText = (slot) =>
    [slot.startTime, slot.endTime].filter(Boolean).join(' to ');

const PRIOR_ROLE_LABELS = {
    ta: 'Teaching Assistant',
    si: 'SI Leader',
    la: 'Learning Assistant',
    tutor: 'Tutor',
    none: 'None of these',
};

// The attendance questions are asked of a new LA only; a returning LA's
// answers are stored unset.
function attendanceText(answer, isReturning) {
    if (answer === true) return 'Yes';
    if (answer === false) return 'No';
    if (isReturning === true) return 'Not asked of a returning LA';
    return 'Not answered';
}

function LaQuestionFields({ answers }) {
    const roles = answers.priorRoles || [];
    return (
        <>
            <Field label="Can attend the LA orientation">
                {attendanceText(
                    answers.canAttendOrientation,
                    answers.isReturning
                )}
            </Field>
            <Field label="Can attend the LA pedagogy seminar">
                {attendanceText(answers.canAttendPedagogy, answers.isReturning)}
            </Field>
            <Field label="Paid peer-support roles held">
                {roles.length > 0
                    ? roles
                          .map((role) => PRIOR_ROLE_LABELS[role] || role)
                          .join(', ')
                    : 'Not answered'}
            </Field>
        </>
    );
}

function References({ references }) {
    return (
        <Field label="References">
            <ul className="list-unstyled mb-0">
                {references.map((reference, index) => (
                    <li key={index}>
                        {[
                            reference.name,
                            reference.relationship,
                            reference.email,
                        ]
                            .filter(Boolean)
                            .join(', ')}
                        {reference.formSubmitted
                            ? ' (form submitted)'
                            : ' (form not submitted)'}
                    </li>
                ))}
            </ul>
        </Field>
    );
}

/**
 * The student's own answers on one candidacy, read field by field. The server
 * decides which answers each audience receives, and this shows what arrived:
 * the student's yes/no answers to the program's requirements for this
 * position (no course list or grade is collected). A Learning
 * Assistant profile (one that carries `laDetails`) shows the essays and the
 * LA answers through the program's own `Summary`, and for faculty also the
 * pool and campus employment answers and the eligibility answers (the
 * coordinators read those in their program records). The resume downloads
 * through the candidacy for faculty. Without a program, `laAnswers` (the LA
 * questions' answers: `isReturning`, `canAttendOrientation`,
 * `canAttendPedagogy`, `priorRoles`) are listed field by field, which is how
 * the general pool reads them.
 */
export default function CandidateAnswers({
    profile,
    mode,
    program,
    candidacyId,
    laAnswers,
    courseLabel,
}) {
    if (!profile) {
        return <p className="mb-0">No application answers are on file.</p>;
    }
    const isFaculty = mode !== 'coordinator';
    const requirements = profile.requirementAttestations || [];
    const references = isFaculty ? profile.references || [] : [];
    const slots = profile.availability?.schedule || [];
    const hours = profile.availability?.hoursPerWeek;
    const ProgramSummary = profile.laDetails
        ? getProgramDefinition(program)?.Summary
        : null;
    // Faculty download through the candidacy; a coordinator through the
    // application the profile belongs to.
    const fetchResume =
        isFaculty && candidacyId
            ? () => candidacyService.downloadResume(candidacyId)
            : undefined;

    return (
        <>
            <dl className="mb-0">
                {profile.major && <Field label="Major">{profile.major}</Field>}
                {requirements.length > 0 && (
                    <Field label="Answers for this position">
                        <RequirementAnswersSummary
                            answers={requirements}
                            courseLabel={courseLabel}
                        />
                    </Field>
                )}
                {!ProgramSummary && profile.resume && (
                    <Field label="Resume">
                        <ResumeAnswer
                            application={{
                                resume: profile.resume,
                                id: profile.id,
                            }}
                            fetchResume={fetchResume}
                        />
                    </Field>
                )}
                {slots.length > 0 && (
                    <Field label="Days and times available">
                        <ul className="list-unstyled mb-0">
                            {slots.map((slot, index) => (
                                <li key={index}>
                                    {[slot.day, timeText(slot)]
                                        .filter(Boolean)
                                        .join(', ')}
                                </li>
                            ))}
                        </ul>
                    </Field>
                )}
                {hours != null && (
                    <Field label="Hours available a week">{hours}</Field>
                )}
                {!ProgramSummary && profile.priorExperience && (
                    <Field label="Prior experience">
                        <Paragraphs value={profile.priorExperience} />
                    </Field>
                )}
                {!ProgramSummary && profile.interestStatement && (
                    <Field label="Interest statement">
                        <Paragraphs value={profile.interestStatement} />
                    </Field>
                )}
                {references.length > 0 && (
                    <References references={references} />
                )}
                {laAnswers && <LaQuestionFields answers={laAnswers} />}
            </dl>
            {ProgramSummary && (
                <div className="mt-3">
                    <ProgramSummary
                        application={profile}
                        fetchResume={fetchResume}
                    />
                    {isFaculty && (
                        <>
                            <SharedAnswersSummary
                                application={{
                                    generalPool: profile.generalPool,
                                    campusEmployment: profile.campusEmployment,
                                }}
                            />
                            {profile.employmentAttestation && (
                                <EmploymentAttestationSummary
                                    attestation={profile.employmentAttestation}
                                    audience="faculty"
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </>
    );
}
